import React, { useEffect, useMemo, useState } from "react";
import CustomSelect from "../../components/CustomSelect.jsx";
import {
    createManagerSeasonDriver,
    fetchManagerDrivers,
    fetchManagerSeriesSeasons,
    updateManagerDriver,
    updateManagerSeasonDriver,
    uploadManagerDriversCsv,
} from "../../lib/managerApi.js";

const STATUS_OPTIONS = [
    { value: "active", label: "Aktiv" },
    { value: "inactive", label: "Inaktiv" },
];

const STATUS_FILTER_OPTIONS = [
    { value: "all", label: "Alle Fahrer" },
    { value: "active", label: "Nur aktiv" },
    { value: "inactive", label: "Nur inaktiv" },
];

const CLASS_OPTIONS = [
    { value: "PRO", label: "PRO" },
    { value: "INT", label: "INT" },
    { value: "AM", label: "AM" },
];

function prettyError(err) {
    if (!err) return "Unbekannter Fehler";
    if (typeof err === "string") return err;
    if (err instanceof Error) return err.message || "Fehler";
    if (err.detail) return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
    return String(err);
}

function driverName(driver) {
    return driver?.display_name || driver?.iracing_name || driver?.label || "Fahrer";
}

function seasonDriverLabel(driver) {
    const sd = driver?.season_driver || driver?.season_entries?.[0];
    const number = sd?.car_number ? `#${sd.car_number}` : "";
    return [number, driverName(driver)].filter(Boolean).join(" ") || "Fahrer";
}

function discordLabel(user) {
    if (!user) return "Nicht verknuepft";
    return user.display_name || user.discord_global_name || user.discord_username || `Discord #${user.id}`;
}

function statusLabel(status) {
    return status === "inactive" ? "Inaktiv" : "Aktiv";
}

function StatusPill({ status }) {
    const inactive = status === "inactive";

    return (
        <span className={inactive ? "pill pill--danger" : "pill pill--am"}>
            {statusLabel(status)}
        </span>
    );
}

function Field({ label, children }) {
    return (
        <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontWeight: 800, fontSize: 13 }}>{label}</span>
            {children}
        </label>
    );
}

export default function DriverManagement() {
    const [seriesRows, setSeriesRows] = useState([]);
    const [seriesKey, setSeriesKey] = useState("gt3x");
    const [seasonId, setSeasonId] = useState("all");
    const [seasonDriverSeasonId, setSeasonDriverSeasonId] = useState("");
    const [search, setSearch] = useState("");
    const [driverStatus, setDriverStatus] = useState("all");

    const [drivers, setDrivers] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [selectedSeason, setSelectedSeason] = useState(null);

    const [driverForm, setDriverForm] = useState({
        iracing_name: "",
        iracing_id: "",
        display_name: "",
        status: "active",
    });
    const [seasonForm, setSeasonForm] = useState({
        grid_position: "",
        car_number: "",
        class_name: "",
        team_name: "",
        status: "active",
    });

    const [importFile, setImportFile] = useState(null);
    const [importSeasonId, setImportSeasonId] = useState("");

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const selectedDriver = useMemo(() => {
        return drivers.find((driver) => String(driver.id) === String(selectedDriverId)) || null;
    }, [drivers, selectedDriverId]);

    const selectedSeries = useMemo(() => {
        return seriesRows.find((series) => series.series_key === seriesKey) || null;
    }, [seriesRows, seriesKey]);

    const seasonEditOptions = useMemo(() => {
        return (selectedSeries?.seasons || []).map((season) => ({
            value: season.id,
            label: `${season.display_name} (${season.status})`,
        }));
    }, [selectedSeries]);

    const seasonFilterOptions = useMemo(() => {
        return [
            { value: "all", label: "Alle Fahrer" },
            ...seasonEditOptions,
        ];
    }, [seasonEditOptions]);

    const seriesOptions = useMemo(() => {
        return seriesRows.map((series) => ({
            value: series.series_key,
            label: series.display_name,
        }));
    }, [seriesRows]);

    const selectedSeasonForEdit = useMemo(() => {
        return selectedSeries?.seasons?.find((season) => String(season.id) === String(seasonDriverSeasonId)) || null;
    }, [selectedSeries, seasonDriverSeasonId]);

    const selectedSeasonEntry = useMemo(() => {
        return selectedDriver?.season_entries?.find((entry) => String(entry.season_id) === String(seasonDriverSeasonId)) || null;
    }, [selectedDriver, seasonDriverSeasonId]);

    useEffect(() => {
        let active = true;

        async function loadSeries() {
            try {
                setLoading(true);
                setError("");
                const data = await fetchManagerSeriesSeasons();
                if (!active) return;

                const nextSeries = Array.isArray(data.series) ? data.series : [];
                setSeriesRows(nextSeries);

                const preferred = nextSeries.find((series) => series.series_key === "gt3x") || nextSeries[0];
                if (preferred) {
                    setSeriesKey((current) => current || preferred.series_key);
                    const activeSeason =
                        preferred.seasons?.find((season) => season.status === "active") ||
                        preferred.seasons?.[0];
                    setSeasonId("all");
                    if (activeSeason) {
                        setSeasonDriverSeasonId(String(activeSeason.id));
                        setImportSeasonId(String(activeSeason.id));
                    }
                }
            } catch (err) {
                if (active) setError(prettyError(err));
            } finally {
                if (active) setLoading(false);
            }
        }

        loadSeries();
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        const series = seriesRows.find((row) => row.series_key === seriesKey);
        if (!series) return;

        const hasCurrentSeason = seasonId === "all" || series.seasons?.some((season) => String(season.id) === String(seasonId));
        const hasEditSeason = series.seasons?.some((season) => String(season.id) === String(seasonDriverSeasonId));

        const activeSeason =
            series.seasons?.find((season) => season.status === "active") ||
            series.seasons?.[0];
        if (!hasCurrentSeason) setSeasonId("all");
        if (!hasEditSeason) setSeasonDriverSeasonId(activeSeason ? String(activeSeason.id) : "");
        setImportSeasonId((current) => current || (activeSeason ? String(activeSeason.id) : ""));
    }, [seriesRows, seriesKey, seasonId, seasonDriverSeasonId]);

    async function loadDrivers() {
        if (!seriesKey) return;

        try {
            setLoading(true);
            setError("");
            const allSeasons = seasonId === "all";
            const data = await fetchManagerDrivers({
                series_key: seriesKey,
                season_id: allSeasons ? "" : seasonId,
                all_seasons: allSeasons ? "true" : "",
                search,
                driver_status: driverStatus,
            });

            const rows = Array.isArray(data.drivers) ? data.drivers : [];
            setDrivers(rows);
            setSelectedSeason(data.season || null);

            setSelectedDriverId((current) => {
                if (current && rows.some((driver) => String(driver.id) === String(current))) return current;
                return rows[0]?.id ? String(rows[0].id) : "";
            });
        } catch (err) {
            setError(prettyError(err));
            setDrivers([]);
            setSelectedDriverId("");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!seriesKey) return;
        loadDrivers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seriesKey, seasonId, driverStatus]);

    useEffect(() => {
        if (!selectedDriver) {
            setDriverForm({
                iracing_name: "",
                iracing_id: "",
                display_name: "",
                status: "active",
            });
            setSeasonForm({
                grid_position: "",
                car_number: "",
                class_name: "",
                team_name: "",
                status: "active",
            });
            return;
        }

        setDriverForm({
            iracing_name: selectedDriver.iracing_name || "",
            iracing_id: selectedDriver.iracing_id || "",
            display_name: selectedDriver.display_name || "",
            status: selectedDriver.status || "active",
        });

        setSeasonForm({
            grid_position: selectedSeasonEntry?.grid_position ? String(selectedSeasonEntry.grid_position) : "",
            car_number: selectedSeasonEntry?.car_number || "",
            class_name: selectedSeasonEntry?.class_name || "",
            team_name: selectedSeasonEntry?.team_name || "",
            status: selectedSeasonEntry?.status || "active",
        });
    }, [selectedDriver, selectedSeasonEntry]);

    function patchDriverInList(nextDriver) {
        if (!nextDriver?.id) return;
        setDrivers((prev) => prev.map((driver) => (
            String(driver.id) === String(nextDriver.id)
                ? {
                    ...driver,
                    ...nextDriver,
                    season_driver: nextDriver.season_driver || driver.season_driver,
                }
                : driver
        )));
    }

    async function saveDriver() {
        if (!selectedDriver) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const data = await updateManagerDriver(selectedDriver.id, {
                ...driverForm,
                series_key: seriesKey,
            });
            patchDriverInList(data.driver);
            setSuccess("Fahrer wurde gespeichert.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function saveSeasonDriver() {
        if (!selectedSeasonEntry?.id) return;

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const data = await updateManagerSeasonDriver(selectedSeasonEntry.id, {
                ...seasonForm,
                grid_position: seasonForm.grid_position ? Number(seasonForm.grid_position) : undefined,
            });
            patchDriverInList(data.driver);
            setSuccess("Saisonfahrer wurde gespeichert.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function assignSeasonDriver() {
        if (!selectedDriver || !selectedSeasonForEdit?.id) return;

        if (!seasonForm.grid_position) {
            setError("Bitte eine Position fuer den Saisonfahrer eintragen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const data = await createManagerSeasonDriver({
                season_id: Number(selectedSeasonForEdit.id),
                driver_id: Number(selectedDriver.id),
                grid_position: Number(seasonForm.grid_position),
                car_number: seasonForm.car_number,
                class_name: seasonForm.class_name,
                team_name: seasonForm.team_name,
                status: seasonForm.status || "active",
            });
            patchDriverInList(data.driver);
            setSuccess("Fahrer wurde der Saison zugeordnet.");
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    async function uploadCsv() {
        if (!importFile) {
            setError("Bitte eine CSV-Datei auswaehlen.");
            return;
        }

        try {
            setBusy(true);
            setError("");
            setSuccess("");
            const data = await uploadManagerDriversCsv({
                file: importFile,
                seriesKey,
                seasonId: importSeasonId === "none" ? "" : importSeasonId,
            });

            const summary = data.summary || {};
            const errorCount = Array.isArray(summary.errors) ? summary.errors.length : 0;
            setSuccess(
                `CSV importiert: Fahrer neu ${summary.driver_created || 0}, Fahrer aktualisiert ${summary.driver_updated || 0}, ` +
                `Saisonfahrer neu ${summary.season_driver_created || 0}, Saisonfahrer aktualisiert ${summary.season_driver_updated || 0}` +
                (errorCount ? `. Fehlerhafte Zeilen: ${errorCount}` : ".")
            );
            await loadDrivers();
        } catch (err) {
            setError(prettyError(err));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Liga Manager</div>
                        <h1 className="rv-page-title">Fahrerverwaltung</h1>
                        <p className="rv-page-subtitle">
                            Fahrer suchen, Stammdaten pflegen und Saisonfahrer fuer die aktive Liga bearbeiten.
                        </p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}
            {success && <div className="rv-message rv-message--ok">{success}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 14 }}>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                        gap: 12,
                    }}
                >
                    <Field label="Serie">
                        <CustomSelect
                            value={seriesKey}
                            onChange={setSeriesKey}
                            options={seriesOptions}
                            allowEmpty={false}
                        />
                    </Field>

                    <Field label="Saison">
                        <CustomSelect
                            value={seasonId}
                            onChange={setSeasonId}
                            options={seasonFilterOptions}
                            allowEmpty={false}
                            disabled={!seasonFilterOptions.length}
                        />
                    </Field>

                    <Field label="Fahrerstatus">
                        <CustomSelect
                            value={driverStatus}
                            onChange={setDriverStatus}
                            options={STATUS_FILTER_OPTIONS}
                            allowEmpty={false}
                        />
                    </Field>

                    <Field label="Suche">
                        <input
                            className="input"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === "Enter") loadDrivers();
                            }}
                            placeholder="Name, iRacing ID ..."
                        />
                    </Field>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <button className="btn btn--accent" type="button" onClick={loadDrivers} disabled={loading}>
                        {loading ? "Lade ..." : "Fahrer laden"}
                    </button>
                    {selectedSeason && (
                        <span className="muted">
                            Aktuelle Auswahl: {selectedSeason.display_name} ({selectedSeason.status})
                        </span>
                    )}
                    {!selectedSeason && seasonId === "all" && (
                        <span className="muted">
                            Aktuelle Auswahl: Alle Fahrer dieser Serie
                        </span>
                    )}
                </div>
            </div>

            <div className="card rv-filter-card" style={{ display: "grid", gap: 14 }}>
                <div>
                    <h2 style={{ marginTop: 0 }}>CSV Import</h2>
                    <p className="muted" style={{ marginTop: 4 }}>
                        CSV aus Google Sheets hochladen. Ohne Saison werden nur Fahrer-Stammdaten importiert;
                        mit Saison werden zusaetzlich Eintraege in rv_season_drivers angelegt oder aktualisiert.
                    </p>
                </div>

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
                        gap: 12,
                        alignItems: "end",
                    }}
                >
                    <Field label="CSV Datei">
                        <input
                            className="input"
                            type="file"
                            accept=".csv,text/csv"
                            onChange={(event) => setImportFile(event.target.files?.[0] || null)}
                        />
                    </Field>

                    <Field label="Saison fuer Zuordnung">
                        <CustomSelect
                            value={importSeasonId || "none"}
                            onChange={setImportSeasonId}
                            options={[
                                { value: "none", label: "Nur Fahrer-Stammdaten" },
                                ...seasonEditOptions,
                            ]}
                            allowEmpty={false}
                        />
                    </Field>

                    <button className="btn btn--accent" type="button" onClick={uploadCsv} disabled={busy || !importFile}>
                        CSV importieren
                    </button>
                </div>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
                    gap: 16,
                    alignItems: "start",
                }}
            >
                <div className="card" style={{ display: "grid", gap: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <h2 style={{ marginTop: 0 }}>Fahrer</h2>
                        <span className="pill pill--gesamt">{drivers.length}</span>
                    </div>

                    <div style={{ display: "grid", gap: 8, maxHeight: 720, overflow: "auto", paddingRight: 4 }}>
                        {!drivers.length && (
                            <div className="muted">Keine Fahrer gefunden.</div>
                        )}

                        {drivers.map((driver) => {
                            const active = String(driver.id) === String(selectedDriverId);
                            const seasonText = seasonId === "all"
                                ? (
                                    driver.season_entries?.length
                                        ? driver.season_entries
                                            .map((entry) => `${entry.season?.display_name || "Saison"}: ${entry.class_name || "Klasse offen"} / P${entry.grid_position}`)
                                            .join(" | ")
                                        : "Keiner Saison zugeordnet"
                                )
                                : (
                                    driver.season_driver
                                        ? `${driver.season_driver.class_name || "Klasse offen"} / P${driver.season_driver.grid_position}`
                                        : "Nicht in dieser Saison"
                                );

                            return (
                                <button
                                    key={driver.id}
                                    type="button"
                                    onClick={() => setSelectedDriverId(String(driver.id))}
                                    style={{
                                        textAlign: "left",
                                        border: active
                                            ? "1px solid rgba(228,254,60,.46)"
                                            : "1px solid rgba(255,255,255,.10)",
                                        background: active
                                            ? "rgba(228,254,60,.10)"
                                            : "rgba(255,255,255,.035)",
                                        borderRadius: 14,
                                        padding: 12,
                                        color: "var(--text)",
                                        cursor: "pointer",
                                        display: "grid",
                                        gap: 6,
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                        <strong>{seasonDriverLabel(driver)}</strong>
                                        <StatusPill status={driver.status} />
                                    </div>
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        {seasonText}
                                    </div>
                                    <div className="muted" style={{ fontSize: 13 }}>
                                        Discord: {discordLabel(driver.discord_user)}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="card" style={{ display: "grid", gap: 18 }}>
                    {!selectedDriver ? (
                        <div className="muted">Bitte einen Fahrer auswaehlen.</div>
                    ) : (
                        <>
                            <div>
                                <div className="rv-page-kicker">Bearbeiten</div>
                                <h2 style={{ margin: "4px 0 0" }}>{seasonDriverLabel(selectedDriver)}</h2>
                                <p className="muted" style={{ marginTop: 6 }}>
                                    ID {selectedDriver.id} / Discord: {discordLabel(selectedDriver.discord_user)}
                                </p>
                            </div>

                            <section style={{ display: "grid", gap: 12 }}>
                                <h3 style={{ margin: 0 }}>Fahrer-Stammdaten</h3>

                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                        gap: 12,
                                    }}
                                >
                                    <Field label="iRacing Name">
                                        <input
                                            className="input"
                                            value={driverForm.iracing_name}
                                            onChange={(event) => setDriverForm((prev) => ({ ...prev, iracing_name: event.target.value }))}
                                        />
                                    </Field>

                                    <Field label="iRacing ID">
                                        <input
                                            className="input"
                                            value={driverForm.iracing_id}
                                            onChange={(event) => setDriverForm((prev) => ({ ...prev, iracing_id: event.target.value }))}
                                            placeholder="Optional"
                                        />
                                    </Field>

                                    <Field label="Klarname / Anzeige">
                                        <input
                                            className="input"
                                            value={driverForm.display_name}
                                            onChange={(event) => setDriverForm((prev) => ({ ...prev, display_name: event.target.value }))}
                                            placeholder="Optional"
                                        />
                                    </Field>

                                    <Field label="Status">
                                        <CustomSelect
                                            value={driverForm.status}
                                            onChange={(value) => setDriverForm((prev) => ({ ...prev, status: value }))}
                                            options={STATUS_OPTIONS}
                                            allowEmpty={false}
                                        />
                                    </Field>
                                </div>

                                <div>
                                    <button className="btn btn--accent" type="button" onClick={saveDriver} disabled={busy}>
                                        Fahrer speichern
                                    </button>
                                </div>
                            </section>

                            <section style={{ display: "grid", gap: 12 }}>
                                <h3 style={{ margin: 0 }}>Saisonfahrer</h3>

                                <Field label="Saison fuer Saisonfahrer-Daten">
                                    <CustomSelect
                                        value={seasonDriverSeasonId}
                                        onChange={setSeasonDriverSeasonId}
                                        options={seasonEditOptions}
                                        allowEmpty={false}
                                        disabled={!seasonEditOptions.length}
                                    />
                                </Field>

                                {!selectedSeasonEntry ? (
                                    <>
                                        <div className="rv-message rv-message--error">
                                            Dieser Fahrer ist der ausgewaehlten Saison noch nicht zugeordnet.
                                            Trage die Saisonfahrer-Daten ein und ordne ihn dieser Saison zu.
                                        </div>

                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                                                gap: 12,
                                            }}
                                        >
                                            <Field label="Position">
                                                <input
                                                    className="input"
                                                    type="number"
                                                    min="1"
                                                    value={seasonForm.grid_position}
                                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, grid_position: event.target.value }))}
                                                />
                                            </Field>

                                            <Field label="Startnummer">
                                                <input
                                                    className="input"
                                                    value={seasonForm.car_number}
                                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, car_number: event.target.value }))}
                                                />
                                            </Field>

                                            <Field label="Klasse">
                                                <CustomSelect
                                                    value={seasonForm.class_name}
                                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, class_name: value }))}
                                                    options={CLASS_OPTIONS}
                                                />
                                            </Field>

                                            <Field label="Saisonstatus">
                                                <CustomSelect
                                                    value={seasonForm.status}
                                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, status: value }))}
                                                    options={STATUS_OPTIONS}
                                                    allowEmpty={false}
                                                />
                                            </Field>
                                        </div>

                                        <Field label="Team">
                                            <input
                                                className="input"
                                                value={seasonForm.team_name}
                                                onChange={(event) => setSeasonForm((prev) => ({ ...prev, team_name: event.target.value }))}
                                                placeholder="Optional"
                                            />
                                        </Field>

                                        <div>
                                            <button className="btn btn--accent" type="button" onClick={assignSeasonDriver} disabled={busy || !selectedSeasonForEdit?.id}>
                                                Fahrer dieser Saison zuordnen
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                                                gap: 12,
                                            }}
                                        >
                                            <Field label="Position">
                                                <input
                                                    className="input"
                                                    type="number"
                                                    min="1"
                                                    value={seasonForm.grid_position}
                                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, grid_position: event.target.value }))}
                                                />
                                            </Field>

                                            <Field label="Startnummer">
                                                <input
                                                    className="input"
                                                    value={seasonForm.car_number}
                                                    onChange={(event) => setSeasonForm((prev) => ({ ...prev, car_number: event.target.value }))}
                                                />
                                            </Field>

                                            <Field label="Klasse">
                                                <CustomSelect
                                                    value={seasonForm.class_name}
                                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, class_name: value }))}
                                                    options={CLASS_OPTIONS}
                                                />
                                            </Field>

                                            <Field label="Saisonstatus">
                                                <CustomSelect
                                                    value={seasonForm.status}
                                                    onChange={(value) => setSeasonForm((prev) => ({ ...prev, status: value }))}
                                                    options={STATUS_OPTIONS}
                                                    allowEmpty={false}
                                                />
                                            </Field>
                                        </div>

                                        <Field label="Team">
                                            <input
                                                className="input"
                                                value={seasonForm.team_name}
                                                onChange={(event) => setSeasonForm((prev) => ({ ...prev, team_name: event.target.value }))}
                                                placeholder="Optional"
                                            />
                                        </Field>

                                        <div>
                                            <button className="btn btn--accent" type="button" onClick={saveSeasonDriver} disabled={busy}>
                                                Saisonfahrer speichern
                                            </button>
                                        </div>
                                    </>
                                )}
                            </section>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
